import React, { useState, cloneElement } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { X } from "lucide-react";
import { DeleteConfirmIcon } from "@/components/ui/icon";
import { TextButton } from "@/components/common/text-button";

interface DeleteConfirmModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  title?: string;
  description?: string;
  cancel?: string;
  confirm?: string;
  loading?: boolean;
  onConfirm: () => void;
  onOpenChange?: (open: boolean) => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  trigger,
  open: externalOpen,
  title,
  description,
  cancel,
  confirm,
  onConfirm,
  loading = false,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("delete_confirm_modal.title");
  const resolvedDescription = description ?? t("delete_confirm_modal.description");
  const resolvedCancel = cancel ?? t("delete_confirm_modal.cancel");
  const resolvedConfirm = confirm ?? t("delete_confirm_modal.confirm");

  const [internalOpen, setInternalOpen] = useState(false);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  const handleConfirm = async () => {
    await onConfirm();
    handleOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
    } else if (e.key === "Escape") {
      handleOpenChange(false);
    }
  };

  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-xl shadow-xl",
          "w-[400px] max-w-[90vw]",
          "py-4 px-6",
          "z-[1002]"
        )}
        onKeyDown={handleKeyDown}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {/* 图标 */}
        <div className={cn("flex justify-between mb-4")}>
          <DeleteConfirmIcon className={cn("w-10 h-10")} />
          {/* 关闭按钮 */}
          <Dialog.Close asChild>
            <button
              className={cn(
                "flex items-center justify-center w-10 h-10",
                "text-gray-400 hover:text-gray-600 outline-gray-400",
                "transition-colors duration-200"
              )}
              onClick={() => handleOpenChange(false)}
            >
              <X className={cn("w-5 h-5")} />
            </button>
          </Dialog.Close>
        </div>

        {/* 标题 */}
        <Dialog.Title
          className={cn("text-lg font-semibold text-i-normal mb-1")}
        >
          {resolvedTitle}
        </Dialog.Title>

        {/* 描述 */}
        <Dialog.Description className={cn("text-i-[rgba(71,84,103,1)] mb-6 text-sm")}>
          {resolvedDescription}
        </Dialog.Description>

        {/* 按钮组 */}
        <div className={cn("flex gap-3 mb-2")}>
          <button
            onClick={() => handleOpenChange(false)}
            className={cn(
              "flex-1 px-4 py-2.5",
              "text-base text-i-black font-semibold",
              "border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-50 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-200"
            )}
          >
            {resolvedCancel}
          </button>
          <TextButton
            label={resolvedConfirm}
            onClick={() => handleConfirm()}
            loading={loading}
            disabled={loading}
            className={cn(
              "flex-1 px-4 py-2.5",
              "text-base text-i-white font-semibold",
              "bg-i-normal rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-800 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-400",
            )}
          >

          </TextButton>
          {/* <button
            onClick={() => handleConfirm()}
            className={cn(
              "flex-1 px-4 py-2.5",
              "text-base text-i-white font-semibold",
              "bg-i-normal rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
              "hover:bg-gray-800 transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-gray-400",
              loading && "cursor-not-allowed opacity-50"
            )}
          >
            {confirm}
          </button> */}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
};

export default DeleteConfirmModal;
